/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import Checkbox from '@ion/dls/src/components/checkbox/CheckBox';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import TextField from '@ion/dls/src/components/textfield/TextField';
import FilterIcon from '@ion/dls/src/icons/FilterIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import withSignals from '../../components/hoc/withSignals';
import {headerTab, searchValue, sideTab} from './store';
import {Headertabs, SideNavTabs} from './types';
import {getReportFilterOptions} from './utils';

// defines the interface definition of ReportFilter...
interface ReportFilterProps {
  categories: GenericObject[];
  categoryFilter: string[];
  setCategoryFilter: (categories: string[], headerTab?: Headertabs, sideTab?: SideNavTabs) => void;
};

// defines the styles definition of ReportFilter...
export const useStyles = makeStyles(() => ({
  checkboxRoot : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '4px'
  },
  checkbox : {
    margin  : 0,
    padding : 0
  },
  optionLabel : {
    flex        : 1,
    paddingLeft : '8px',
    marginLeft  : '-28px'
  }
}));

/**
 * Renders a multi-select dropdown filter component for report categories.
 * This component allows users to filter reports based on one or more categories.
 * It provides a "Select All" option, checkbox-based selection, and integrates visual 
 * indicators (colored borders) for category differentiation. The dropdown is styled 
 * using Material-UI and DLS components, and it is fully controlled by a global signal
 * store.
 *
 * ### Behavior
 * - When `categories` are passed in, it computes a list of selectable category options.
 * - On mount or when `categories` change, the filter is initialized to include all categories 
 *   by default.
 * - If "Select All" is chosen, all options are selected; deselecting it clears the filter.
 * - Each category is visually represented with a left-colored border via `getFolderColor()`.
 * - The input field is read-only and displays a filter icon if any category is selected.
 * - The `categoryFilter` signal is updated when user selection changes.
 * 
 * ```typescript
 * const categories = [
 *   { id: 'cat1', name: 'Finance', dashboards: [...] },
 *   { id: 'cat2', name: 'Marketing', dashboards: [...] }
 * ];
 *
 * <ReportsFilter categories={categories} />
 * ```
 * @note
 * The actual filtering logic based on `categoryFilter` happens outside this component.
 * This component only manages and updates the selected category options.
 * 
 * @param props - Props containing the list of report categories.
 * @param props.categories - The category objects used to generate filter options.
 *
 * @returns {JSX.Element} The rendered dropdown filter component.
 */
const ReportsFilter = ({categories, categoryFilter, setCategoryFilter}: ReportFilterProps) => {
  const classes    = useStyles();
  const inputRef   = useRef<HTMLInputElement | null>(null);
  const SELECT_ALL = useMemo(() => ({
    label : translate('sisense:analytics.categoryFilter.selectAll'),
    value : '__all__'
  }), []);

  const categoryOptions = useMemo(() => (
    getReportFilterOptions(categories) ?? []
  ), [safeJSONStringify(categories)]);

  const disabled = !categoryOptions?.length|| !!searchValue.value.trim().length;

  useEffect(() => {
    if (!categoryFilter && categoryOptions?.length) {
      const withoutSelectAll = categoryOptions.filter(({value}) => value !== SELECT_ALL.value);
      setCategoryFilter(withoutSelectAll, headerTab.value, sideTab.value);
    }
  }, [categoryOptions, headerTab.value, sideTab.value]);
    
  const allOptions = useMemo(() => [SELECT_ALL, ...categoryOptions], [categoryOptions]);
    
  const isAllSelected = useMemo(() => (
    categoryFilter?.length === categoryOptions.length
  ), [categoryOptions, categoryFilter]);
    
  const isOptionSelected = useCallback(option => {
    if (option.value === SELECT_ALL.value) return isAllSelected;
    return categoryFilter?.some((sel) => sel.value === option.value);
  }, [categoryFilter, isAllSelected]);

  const onChange = useCallback(function(e, newValue) {
    const lastSelected = newValue[newValue.length - 1];
    if (lastSelected?.value === SELECT_ALL.value) {
      if (isAllSelected) {
        setCategoryFilter([]);
      } else {
        setCategoryFilter(categoryOptions);
      }
    } else {
      const withoutSelectAll = newValue.filter(({value}) => value !== SELECT_ALL.value);
      setCategoryFilter(withoutSelectAll);
    }
  }, [isAllSelected, categoryOptions]);

  const allSelected = useMemo(() => (
    categoryFilter?.length === categoryOptions.length
  ), [categoryFilter, categoryOptions]);

  const isIndeterminate = useMemo(() => (
    categoryFilter?.length > 0 && !allSelected
  ), [categoryFilter, allSelected]);

  const findCategoryColor = (categories: GenericObject[], value: string): string => {
    for (const {source, color, folders} of categories) {
      if (source?.oid === value) {
        return color;
      }
      if (folders?.length) {
        const nestedColor = findCategoryColor(folders, value);
        if (nestedColor) {
          return nestedColor;
        }
      }
    }
  };

  const renderOptions = useCallback(function(option) {
    const isAll = option.value === SELECT_ALL.value;
    return (
      <li>
        <div className={classes.checkboxRoot}>
          <div className={classes.checkbox}>
            <Checkbox checked={isAll ? allSelected : isOptionSelected(option)}
              indeterminate={isAll ? isIndeterminate : false}
              style={{marginRight: 8}} />
          </div>
          <div style={isAll ? null : {borderLeft: `6px solid ${findCategoryColor(categories, option.value)}`}}
            className={classes.optionLabel}>
            <span>{option.label}</span>
          </div>
        </div>
      </li>
    );
  }, [allSelected, isIndeterminate, isOptionSelected]);

  const renderTags = useCallback(() => null, []);

  const getInputRef = useCallback(function(ref) {
    inputRef.current = ref;
    if (ref) {
      ref.setAttribute('readonly', 'true');
      ref.setAttribute('unselectable', 'on');
      ref.style.caretColor = 'transparent';
      ref.style.userSelect = 'none';
    }
  }, []);

  const handleAdornmentClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      const event = new KeyboardEvent('keydown', {
        key     : 'ArrowDown',
        code    : 'ArrowDown',
        bubbles : true
      });
      inputRef.current.dispatchEvent(event);
    }
  }, []);

  const renderInput = useCallback(function(params) {
    return (
      <TextField {...params}
        inputRef={getInputRef}
        InputProps={{
          ...params.InputProps,
          readOnly       : true,
          startAdornment : (!disabled && categoryFilter?.length) ? (
            <div style={{cursor: 'pointer', marginTop: '6px'}}
              onClick={handleAdornmentClick} >
              <FilterIcon size={IconSize.largeMedium}
                color={dlsColor(allSelected ? 'caBlue' : 'grey3')} />
            </div>
          ) : null,
          style : {cursor: 'pointer'}
        }}
        inputProps={{
          ...params.inputProps,
          value : translate('sisense:analytics.categoryFilter.name', {
            count : disabled ? translate('common:notAvailable') : allSelected ? translate('sisense:analytics.topNavigation.tabs.all')
              : (categoryFilter?.length ?? 0) === 0 ? translate('common:options.none') : categoryFilter?.length
          }),
          readOnly : true,
          style    : {
            userSelect : 'none'
          }
        }}/>

    );
  }, [categoryFilter, disabled, allSelected]);

  return (
    <Dropdown multiSelect value={(categoryFilter || []) as any}
      disabled={disabled}
      options={allOptions}
      onChange={onChange}
      renderOption={renderOptions}
      renderTags={renderTags}
      renderInput={renderInput}
      disableCloseOnSelect />
  );
};

export default withSignals(ReportsFilter);
